import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { EditorContent } from '@tiptap/react';
import EditorToolbar from '../../../components/editor/EditorToolbar';
import tiptapExtensions from '../../../components/editor/tiptapExtensions';
import useEditorWithDragDrop from '../../../components/editor/useEditorWithDragDrop';
import FileAttachment from '../../../components/FileAttachment/FileAttachment';
import { getManagerBoard, updateManagerBoard } from '../../../api/managerBoardApi';

function BoardEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const currentPage = searchParams.get('page') || 1;
  const search = searchParams.get('search') || '';

  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [files, setFiles] = useState([]);
  const editor = useEditorWithDragDrop(tiptapExtensions);

  useEffect(() => {
    if (!editor) return;

    const loadData = async () => {
      setLoading(true);
      try {
        const data = await getManagerBoard(id);
        setTitle(data?.title || '');
        if (!editor.isDestroyed) {
          editor.commands.setContent(data?.content || '');
        }

        const existingFiles = Array.isArray(data?.files)
          ? data.files
              .filter((file) => ['attach', 'attatch'].includes(String(file?.file_type || '').toLowerCase()))
              .map((file) => ({
              id: file.id,
              name: file.originalName,
              size: file.size,
              type: file.mimeType,
              mimeType: file.mimeType,
              path: file.path,
              originalName: file.originalName,
              isNew: false,
              isDeleted: false,
            }))
          : [];
        setFiles(existingFiles);
      } catch (err) {
        console.error('관리자 게시글 조회 실패:', err);
        alert('관리자 게시글 조회 실패');
        navigate('/manager/board');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, navigate, editor]);

  const handleUpdate = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      alert('제목을 입력해주세요.');
      return;
    }

    try {
      const contentHtml = editor ? editor.getHTML() : '';

      const deletedFileIds = files
        .filter((f) => f.isDeleted && f.id != null)
        .map((f) => f.id);

      const formData = new FormData();
      formData.append('title', title.trim());
      formData.append('content', contentHtml);
      formData.append('deletedFileIds', JSON.stringify(deletedFileIds));
      files
        .filter((f) => f.isNew && f.file)
        .forEach((f) => formData.append('files', f.file));

      await updateManagerBoard(id, formData);
      navigate(`/manager/board/${id}?page=${currentPage}&search=${encodeURIComponent(search)}`);
    } catch (err) {
      console.error('관리자 게시글 수정 실패:', err);
      alert('관리자 게시글 수정 실패');
    }
  };

  if (loading) {
    return (
      <div className="text-center text-muted mt-5">
        <div className="spinner-border text-secondary mb-3" role="status" />
        <p>불러오는 중...</p>
      </div>
    );
  }

  return (
    <div className="container-fluid view-post-edit">
      <div className="row">
        <div className="col-12">
          <form onSubmit={handleUpdate}>
            <div className="mb-3">
              <label className="form-label fw-bold">제목</label>
              <input
                type="text"
                className="form-control"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </div>

            <div className="mb-3">
              <label className="form-label fw-bold">내용</label>
              <div className="border p-2 rounded">
                {editor && <EditorToolbar editor={editor} />}
                <EditorContent editor={editor} className="editor-content" />
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label fw-bold">첨부파일</label>
              <FileAttachment files={files} setFiles={setFiles} mode="edit" />
            </div>

            <div className="d-flex justify-content-end gap-2">
              <button type="submit" className="btn btn-success">
                수정 완료
              </button>
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => navigate(`/manager/board/${id}?page=${currentPage}&search=${encodeURIComponent(search)}`)}
              >
                취소
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default BoardEdit;
